/**
 * hp-delivery-core.js — shared delivery/availability rendering core (HIG-124).
 *
 * ONE source of truth for the delivery label catalog (the 48 localized strings)
 * + the shared helpers, used by:
 *   - assets/hp-collection-card.js  (collection/search cards — compact format)
 *   - assets/hp-delivery.js         (PDP — full format, its own renderer)
 *   - assets/quick-add.js           (Choose-Options drawer — compact format)
 *
 * Loaded GLOBALLY (layout/theme.liquid) so it's available wherever any of those
 * surfaces appears — especially the quick-add drawer, which opens from sliders on
 * pages where the card script isn't present. Plain ES5 IIFE, no deps.
 *
 * Card vs PDP render DIFFERENTLY on purpose (compact "10+" / no unit word / 2
 * lines vs full "mehr als 10 Stück" / 3 lines) — only the catalog + helpers are
 * shared here. The compact renderer (renderCompact) lives here because TWO
 * surfaces use it (card + drawer); the PDP keeps its own full renderer.
 */
(function () {
  if (window.HPDeliveryCore) return;

  // Locale-aware delivery-label catalog. label_keys are stable identifiers (no
  // translation); per-locale text + icon + color + priority resolve from here.
  // For the per-product variants_data metafield, ONLY label_keys + numbers are
  // stored — never text — so T-Lab does not re-translate when qty changes.
  var LABEL_CATALOG = {
    de: {
      instant:        { label_standalone: 'Sofort versandfertig',                            label_lower: 'sofort versandfertig',                            icon: '✓', color: '#4EA72E', icon_color: '#4EA72E', priority: 10 },
      in_1_2_days:    { label_standalone: 'Versandfertig in 1 bis 2 Tagen',                  label_lower: 'versandfertig in 1 bis 2 Tagen',                  icon: '✓', color: '#222222', icon_color: '#222222', priority: 20 },
      in_2_3_days:    { label_standalone: 'Versandfertig in 2 bis 3 Tagen',                  label_lower: 'versandfertig in 2 bis 3 Tagen',                  icon: '✓', color: '#222222', icon_color: '#222222', priority: 25 },
      in_3_4_days:    { label_standalone: 'Versandfertig in 3 bis 4 Tagen',                  label_lower: 'versandfertig in 3 bis 4 Tagen',                  icon: '✓', color: '#222222', icon_color: '#222222', priority: 30 },
      in_4_5_days:    { label_standalone: 'Versandfertig in 4 bis 5 Tagen',                  label_lower: 'versandfertig in 4 bis 5 Tagen',                  icon: '✓', color: '#222222', icon_color: '#222222', priority: 35 },
      in_5_6_days:    { label_standalone: 'Versandfertig in 5 bis 6 Tagen',                  label_lower: 'versandfertig in 5 bis 6 Tagen',                  icon: '✓', color: '#222222', icon_color: '#222222', priority: 40 },
      in_6_7_days:    { label_standalone: 'Versandfertig in 6 bis 7 Tagen',                  label_lower: 'versandfertig in 6 bis 7 Tagen',                  icon: '✓', color: '#222222', icon_color: '#222222', priority: 45 },
      in_7_9_days:    { label_standalone: 'Versandfertig in 7 bis 9 Tagen',                  label_lower: 'versandfertig in 7 bis 9 Tagen',                  icon: '✓', color: '#222222', icon_color: '#222222', priority: 50 },
      in_10_14_days:  { label_standalone: 'Versandfertig in 10 bis 14 Tagen',                label_lower: 'versandfertig in 10 bis 14 Tagen',                icon: '✓', color: '#222222', icon_color: '#222222', priority: 60 },
      in_15_20_days:  { label_standalone: 'Versandfertig in 15 bis 20 Tagen',                label_lower: 'versandfertig in 15 bis 20 Tagen',                icon: '✓', color: '#222222', icon_color: '#222222', priority: 70 },
      in_3_4_weeks:   { label_standalone: 'Versandfertig in 3 bis 4 Wochen',                 label_lower: 'versandfertig in 3 bis 4 Wochen',                 icon: '✓', color: '#8a8a8a', icon_color: '#8a8a8a', priority: 80 },
      in_5_6_weeks:   { label_standalone: 'Versandfertig in 5 bis 6 Wochen',                 label_lower: 'versandfertig in 5 bis 6 Wochen',                 icon: '✓', color: '#8a8a8a', icon_color: '#8a8a8a', priority: 90 },
      in_7_9_weeks:   { label_standalone: 'Versandfertig in 7 bis 9 Wochen',                 label_lower: 'versandfertig in 7 bis 9 Wochen',                 icon: '✓', color: '#8a8a8a', icon_color: '#b5b5b5', priority: 100 },
      in_10_12_weeks: { label_standalone: 'Versandfertig in 10 bis 12 Wochen',               label_lower: 'versandfertig in 10 bis 12 Wochen',               icon: '✓', color: '#8a8a8a', icon_color: '#b5b5b5', priority: 110 },
      in_over_12_weeks: { label_standalone: 'Versandfertig in über 12 Wochen',                  label_lower: 'versandfertig in über 12 Wochen',                 icon: '✓', color: '#8a8a8a', icon_color: '#b5b5b5', priority: 118 },
      over_12_weeks:  { label_standalone: 'Versandfertig in über 12 Wochen (Datum in Abklärung)', label_lower: 'versandfertig in über 12 Wochen (Datum in Abklärung)', icon: '✓', color: '#8a8a8a', icon_color: '#b5b5b5', priority: 120 },
      made_to_order:  { label_standalone: 'Produktion auf Bestellung',                       label_lower: 'Produktion auf Bestellung',                       icon: '✓', color: '#222222', icon_color: '#222222', priority: 115, mto: true },
    },
    en: {
      instant:        { label_standalone: 'Ready to ship',                                   label_lower: 'ready to ship',                                   icon: '✓', color: '#4EA72E', icon_color: '#4EA72E', priority: 10 },
      in_1_2_days:    { label_standalone: 'Ready to ship in 1 to 2 days',                    label_lower: 'ready to ship in 1 to 2 days',                    icon: '✓', color: '#222222', icon_color: '#222222', priority: 20 },
      in_2_3_days:    { label_standalone: 'Ready to ship in 2 to 3 days',                    label_lower: 'ready to ship in 2 to 3 days',                    icon: '✓', color: '#222222', icon_color: '#222222', priority: 25 },
      in_3_4_days:    { label_standalone: 'Ready to ship in 3 to 4 days',                    label_lower: 'ready to ship in 3 to 4 days',                    icon: '✓', color: '#222222', icon_color: '#222222', priority: 30 },
      in_4_5_days:    { label_standalone: 'Ready to ship in 4 to 5 days',                    label_lower: 'ready to ship in 4 to 5 days',                    icon: '✓', color: '#222222', icon_color: '#222222', priority: 35 },
      in_5_6_days:    { label_standalone: 'Ready to ship in 5 to 6 days',                    label_lower: 'ready to ship in 5 to 6 days',                    icon: '✓', color: '#222222', icon_color: '#222222', priority: 40 },
      in_6_7_days:    { label_standalone: 'Ready to ship in 6 to 7 days',                    label_lower: 'ready to ship in 6 to 7 days',                    icon: '✓', color: '#222222', icon_color: '#222222', priority: 45 },
      in_7_9_days:    { label_standalone: 'Ready to ship in 7 to 9 days',                    label_lower: 'ready to ship in 7 to 9 days',                    icon: '✓', color: '#222222', icon_color: '#222222', priority: 50 },
      in_10_14_days:  { label_standalone: 'Ready to ship in 10 to 14 days',                  label_lower: 'ready to ship in 10 to 14 days',                  icon: '✓', color: '#222222', icon_color: '#222222', priority: 60 },
      in_15_20_days:  { label_standalone: 'Ready to ship in 15 to 20 days',                  label_lower: 'ready to ship in 15 to 20 days',                  icon: '✓', color: '#222222', icon_color: '#222222', priority: 70 },
      in_3_4_weeks:   { label_standalone: 'Ready to ship in 3 to 4 weeks',                   label_lower: 'ready to ship in 3 to 4 weeks',                   icon: '✓', color: '#8a8a8a', icon_color: '#8a8a8a', priority: 80 },
      in_5_6_weeks:   { label_standalone: 'Ready to ship in 5 to 6 weeks',                   label_lower: 'ready to ship in 5 to 6 weeks',                   icon: '✓', color: '#8a8a8a', icon_color: '#8a8a8a', priority: 90 },
      in_7_9_weeks:   { label_standalone: 'Ready to ship in 7 to 9 weeks',                   label_lower: 'ready to ship in 7 to 9 weeks',                   icon: '✓', color: '#8a8a8a', icon_color: '#b5b5b5', priority: 100 },
      in_10_12_weeks: { label_standalone: 'Ready to ship in 10 to 12 weeks',                 label_lower: 'ready to ship in 10 to 12 weeks',                 icon: '✓', color: '#8a8a8a', icon_color: '#b5b5b5', priority: 110 },
      in_over_12_weeks: { label_standalone: 'Ready to ship in over 12 weeks',                    label_lower: 'ready to ship in over 12 weeks',                  icon: '✓', color: '#8a8a8a', icon_color: '#b5b5b5', priority: 118 },
      over_12_weeks:  { label_standalone: 'Ready to ship in over 12 weeks (date to be confirmed)', label_lower: 'ready to ship in over 12 weeks (date to be confirmed)', icon: '✓', color: '#8a8a8a', icon_color: '#b5b5b5', priority: 120 },
      made_to_order:  { label_standalone: 'Made to order',                                   label_lower: 'made to order',                                   icon: '✓', color: '#222222', icon_color: '#222222', priority: 115, mto: true },
    },
    fr: {
      instant:        { label_standalone: 'Prêt à expédier',                                 label_lower: 'prêt à expédier',                                 icon: '✓', color: '#4EA72E', icon_color: '#4EA72E', priority: 10 },
      in_1_2_days:    { label_standalone: 'Prêt à expédier en 1 à 2 jours',                  label_lower: 'prêt à expédier en 1 à 2 jours',                  icon: '✓', color: '#222222', icon_color: '#222222', priority: 20 },
      in_2_3_days:    { label_standalone: 'Prêt à expédier en 2 à 3 jours',                  label_lower: 'prêt à expédier en 2 à 3 jours',                  icon: '✓', color: '#222222', icon_color: '#222222', priority: 25 },
      in_3_4_days:    { label_standalone: 'Prêt à expédier en 3 à 4 jours',                  label_lower: 'prêt à expédier en 3 à 4 jours',                  icon: '✓', color: '#222222', icon_color: '#222222', priority: 30 },
      in_4_5_days:    { label_standalone: 'Prêt à expédier en 4 à 5 jours',                  label_lower: 'prêt à expédier en 4 à 5 jours',                  icon: '✓', color: '#222222', icon_color: '#222222', priority: 35 },
      in_5_6_days:    { label_standalone: 'Prêt à expédier en 5 à 6 jours',                  label_lower: 'prêt à expédier en 5 à 6 jours',                  icon: '✓', color: '#222222', icon_color: '#222222', priority: 40 },
      in_6_7_days:    { label_standalone: 'Prêt à expédier en 6 à 7 jours',                  label_lower: 'prêt à expédier en 6 à 7 jours',                  icon: '✓', color: '#222222', icon_color: '#222222', priority: 45 },
      in_7_9_days:    { label_standalone: 'Prêt à expédier en 7 à 9 jours',                  label_lower: 'prêt à expédier en 7 à 9 jours',                  icon: '✓', color: '#222222', icon_color: '#222222', priority: 50 },
      in_10_14_days:  { label_standalone: 'Prêt à expédier en 10 à 14 jours',                label_lower: 'prêt à expédier en 10 à 14 jours',                icon: '✓', color: '#222222', icon_color: '#222222', priority: 60 },
      in_15_20_days:  { label_standalone: 'Prêt à expédier en 15 à 20 jours',                label_lower: 'prêt à expédier en 15 à 20 jours',                icon: '✓', color: '#222222', icon_color: '#222222', priority: 70 },
      in_3_4_weeks:   { label_standalone: 'Prêt à expédier en 3 à 4 semaines',               label_lower: 'prêt à expédier en 3 à 4 semaines',               icon: '✓', color: '#8a8a8a', icon_color: '#8a8a8a', priority: 80 },
      in_5_6_weeks:   { label_standalone: 'Prêt à expédier en 5 à 6 semaines',               label_lower: 'prêt à expédier en 5 à 6 semaines',               icon: '✓', color: '#8a8a8a', icon_color: '#8a8a8a', priority: 90 },
      in_7_9_weeks:   { label_standalone: 'Prêt à expédier en 7 à 9 semaines',               label_lower: 'prêt à expédier en 7 à 9 semaines',               icon: '✓', color: '#8a8a8a', icon_color: '#b5b5b5', priority: 100 },
      in_10_12_weeks: { label_standalone: 'Prêt à expédier en 10 à 12 semaines',             label_lower: 'prêt à expédier en 10 à 12 semaines',             icon: '✓', color: '#8a8a8a', icon_color: '#b5b5b5', priority: 110 },
      in_over_12_weeks: { label_standalone: 'Prêt à expédier en plus de 12 semaines',            label_lower: 'prêt à expédier en plus de 12 semaines',          icon: '✓', color: '#8a8a8a', icon_color: '#b5b5b5', priority: 118 },
      over_12_weeks:  { label_standalone: 'Prêt à expédier en plus de 12 semaines (date à confirmer)', label_lower: 'prêt à expédier en plus de 12 semaines (date à confirmer)', icon: '✓', color: '#8a8a8a', icon_color: '#b5b5b5', priority: 120 },
      made_to_order:  { label_standalone: 'Fabrication sur commande',                        label_lower: 'fabrication sur commande',                        icon: '✓', color: '#222222', icon_color: '#222222', priority: 115, mto: true },
    },
    it: {
      instant:        { label_standalone: 'Pronto per la spedizione',                        label_lower: 'pronto per la spedizione',                        icon: '✓', color: '#4EA72E', icon_color: '#4EA72E', priority: 10 },
      in_1_2_days:    { label_standalone: 'Pronto per la spedizione in 1-2 giorni',          label_lower: 'pronto per la spedizione in 1-2 giorni',          icon: '✓', color: '#222222', icon_color: '#222222', priority: 20 },
      in_2_3_days:    { label_standalone: 'Pronto per la spedizione in 2-3 giorni',          label_lower: 'pronto per la spedizione in 2-3 giorni',          icon: '✓', color: '#222222', icon_color: '#222222', priority: 25 },
      in_3_4_days:    { label_standalone: 'Pronto per la spedizione in 3-4 giorni',          label_lower: 'pronto per la spedizione in 3-4 giorni',          icon: '✓', color: '#222222', icon_color: '#222222', priority: 30 },
      in_4_5_days:    { label_standalone: 'Pronto per la spedizione in 4-5 giorni',          label_lower: 'pronto per la spedizione in 4-5 giorni',          icon: '✓', color: '#222222', icon_color: '#222222', priority: 35 },
      in_5_6_days:    { label_standalone: 'Pronto per la spedizione in 5-6 giorni',          label_lower: 'pronto per la spedizione in 5-6 giorni',          icon: '✓', color: '#222222', icon_color: '#222222', priority: 40 },
      in_6_7_days:    { label_standalone: 'Pronto per la spedizione in 6-7 giorni',          label_lower: 'pronto per la spedizione in 6-7 giorni',          icon: '✓', color: '#222222', icon_color: '#222222', priority: 45 },
      in_7_9_days:    { label_standalone: 'Pronto per la spedizione in 7-9 giorni',          label_lower: 'pronto per la spedizione in 7-9 giorni',          icon: '✓', color: '#222222', icon_color: '#222222', priority: 50 },
      in_10_14_days:  { label_standalone: 'Pronto per la spedizione in 10-14 giorni',        label_lower: 'pronto per la spedizione in 10-14 giorni',        icon: '✓', color: '#222222', icon_color: '#222222', priority: 60 },
      in_15_20_days:  { label_standalone: 'Pronto per la spedizione in 15-20 giorni',        label_lower: 'pronto per la spedizione in 15-20 giorni',        icon: '✓', color: '#222222', icon_color: '#222222', priority: 70 },
      in_3_4_weeks:   { label_standalone: 'Pronto per la spedizione in 3-4 settimane',       label_lower: 'pronto per la spedizione in 3-4 settimane',       icon: '✓', color: '#8a8a8a', icon_color: '#8a8a8a', priority: 80 },
      in_5_6_weeks:   { label_standalone: 'Pronto per la spedizione in 5-6 settimane',       label_lower: 'pronto per la spedizione in 5-6 settimane',       icon: '✓', color: '#8a8a8a', icon_color: '#8a8a8a', priority: 90 },
      in_7_9_weeks:   { label_standalone: 'Pronto per la spedizione in 7-9 settimane',       label_lower: 'pronto per la spedizione in 7-9 settimane',       icon: '✓', color: '#8a8a8a', icon_color: '#b5b5b5', priority: 100 },
      in_10_12_weeks: { label_standalone: 'Pronto per la spedizione in 10-12 settimane',     label_lower: 'pronto per la spedizione in 10-12 settimane',     icon: '✓', color: '#8a8a8a', icon_color: '#b5b5b5', priority: 110 },
      in_over_12_weeks: { label_standalone: 'Pronto per la spedizione in oltre 12 settimane',    label_lower: 'pronto per la spedizione in oltre 12 settimane',  icon: '✓', color: '#8a8a8a', icon_color: '#b5b5b5', priority: 118 },
      over_12_weeks:  { label_standalone: 'Pronto per la spedizione in oltre 12 settimane (data da confermare)', label_lower: 'pronto per la spedizione in oltre 12 settimane (data da confermare)', icon: '✓', color: '#8a8a8a', icon_color: '#b5b5b5', priority: 120 },
      made_to_order:  { label_standalone: 'Produzione su ordinazione',                       label_lower: 'produzione su ordinazione',                       icon: '✓', color: '#222222', icon_color: '#222222', priority: 115, mto: true },
    },
  };

  // Textbausteine für die Verzögerungs-Pille im Cart-Footer. %L ist der
  // lokalisierte Zeitteil einer Stufe, %N die Anzahl und %M der bereits mit
  // einem Leerzeichen beginnende Made-to-order-Suffix.
  var PILL_STR = {
    de: {
      single_only: 'Dieser Artikel ist erst %L versandfertig',
      one: 'Ein Artikel ist erst %L versandfertig',
      many: '%N Artikel sind erst %L versandfertig',
      mixed: '%N Artikel sind erst später versandfertig',
      mto_single: 'Dieser Artikel wird auf Bestellung gefertigt%M',
      mto_one: 'Ein Artikel wird auf Bestellung gefertigt%M',
    },
    en: {
      single_only: 'This item will only be ready to ship %L',
      one: 'One item will only be ready to ship %L',
      many: '%N items will only be ready to ship %L',
      mixed: '%N items will be ready to ship later',
      mto_single: 'This item is made to order%M',
      mto_one: 'One item is made to order%M',
    },
    fr: {
      single_only: 'Cet article ne sera prêt à expédier qu’%L',
      one: 'Un article ne sera prêt à expédier qu’%L',
      many: '%N articles ne seront prêts à expédier qu’%L',
      mixed: '%N articles seront prêts à expédier plus tard',
      mto_single: 'Cet article est fabriqué sur commande%M',
      mto_one: 'Un article est fabriqué sur commande%M',
    },
    it: {
      single_only: 'Questo articolo sarà pronto per la spedizione solo %L',
      one: 'Un articolo sarà pronto per la spedizione solo %L',
      many: '%N articoli saranno pronti per la spedizione solo %L',
      mixed: '%N articoli saranno pronti per la spedizione più tardi',
      mto_single: 'Questo articolo viene prodotto su ordinazione%M',
      mto_one: 'Un articolo viene prodotto su ordinazione%M',
    },
  };

  // Checkbox-Zeile «Teillieferung» in der Verzögerungs-Box (Mockup Runde 15,
  // Entscheid Tobias 2026-08-09). %P = fertig formatierter Preis (fett), vom
  // Aufrufer aus dem Liquid-Preisattribut eingesetzt.
  // Kein Leerzeichen nach dem Plus (Tobias 2026-08-10): «+CHF 9.90».
  var TEIL_STR = {
    de: 'Teillieferung sofort versandfertiger Artikel +%P',
    en: 'Partial delivery of ready-to-ship items +%P',
    fr: 'Livraison partielle des articles prêts à expédier +%P',
    it: 'Consegna parziale degli articoli pronti +%P',
  };

  // Zahlwörter für zwei bis zwölf; ab 13 zeigt der Aufrufer die Ziffer.
  var NUMBER_WORDS = {
    de: ['Zwei', 'Drei', 'Vier', 'Fünf', 'Sechs', 'Sieben', 'Acht', 'Neun', 'Zehn', 'Elf', 'Zwölf'],
    en: ['Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve'],
    fr: ['Deux', 'Trois', 'Quatre', 'Cinq', 'Six', 'Sept', 'Huit', 'Neuf', 'Dix', 'Onze', 'Douze'],
    it: ['Due', 'Tre', 'Quattro', 'Cinque', 'Sei', 'Sette', 'Otto', 'Nove', 'Dieci', 'Undici', 'Dodici'],
  };

  var ACTIVE_LOCALE = (function () {
    var sl = (window.Shopify && window.Shopify.locale) || 'de';
    var short = String(sl).toLowerCase().split('-')[0];
    return LABEL_CATALOG[short] ? short : 'de';
  })();

  function lookupLabel(labelKey) {
    return (LABEL_CATALOG[ACTIVE_LOCALE] && LABEL_CATALOG[ACTIVE_LOCALE][labelKey]) || LABEL_CATALOG.de[labelKey] || null;
  }
  function priorityOf(breakdownEntry) {
    var meta = lookupLabel(breakdownEntry.label_key);
    return meta ? meta.priority : 999;
  }

  // Finales Design (Tobias 2026-06-12): JEDE Lieferzeile bekommt einen Haken als
  // Inline-SVG (statt Schriftzeichen ✓●◐○ — identisch auf jedem OS, bricht nie).
  // Farbe kommt aus icon_color im Katalog. Haken-Entscheid Tobias 2026-07-30
  // (Lieferzeit-Redesign, docs/LIEFERZEIT_KONZEPT.md): GRÜN ist exklusiv für
  // «sofort» reserviert (Text UND Haken); die Tages-Stufen 1-2 bis 7-9 tragen den
  // schwarzen Haken, ab Wochen grau, ab 7 Wochen hellgrau. color = TEXTfarbe.
  // Cart-PROPERTY-Strings (Text, kein HTML) verwenden weiterhin das ✓-Zeichen.
  function checkSvg(color, size) {
    var s = size || 13;
    return '<svg viewBox="0 0 24 24" width="' + s + '" height="' + s + '" fill="none" stroke="' + (color || 'currentColor') + '" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M4 13l5 5L20 6"/></svg>';
  }

  // HIG-128: outline bell (option C), sized to the calling surface (card 13px,
  // PDP 15px). currentColor inherits the OOS-line grey; inline SVG = identical on
  // every OS/browser.
  function bellSvg(size) {
    var s = size || 13;
    return '<svg class="hp-card__delivery-bell" viewBox="0 0 24 24" width="' + s + '" height="' + s + '" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M14.857 17.08a23.8 23.8 0 0 0 5.454-1.31A8.97 8.97 0 0 1 18 9.75V9A6 6 0 1 0 6 9v.75a8.97 8.97 0 0 1-2.312 6.02 23.8 23.8 0 0 0 5.454 1.31m5.715 0a24.3 24.3 0 0 1-5.715 0m5.715 0a3 3 0 1 1-5.715 0"/></svg>';
  }

  // ---- Compact renderer (collection/search cards + Choose-Options drawer) ----
  // Format per line: "<icon> <qty|10+> <label_lower>" — NO unit word ("Stück"),
  // qty capped at "10+", max 2 lines, OOS = bell + "Ausverkauft, Nachschub geplant".
  // Icon column + text column (flex) so a wrapped 2nd line hangs under the text.
  var QTY_DISPLAY_CAP = 10;
  var MORE_THAN_CAP = { de: '10+', en: '10+', fr: '10+', it: '10+' };
  var UNAVAILABLE_COMPACT = { de: 'Ausverkauft, Nachschub geplant', en: 'Sold out, restock planned', fr: 'Épuisé, réapprovisionnement prévu', it: 'Esaurito, rifornimento previsto' };
  // Auslaufend (Produkt-Tag «auslaufend», Entscheid Tobias 2026-07-30): auf den
  // Cards nur noch das nackte «Ausverkauft» — es kommt kein Nachschub mehr.
  var UNAVAILABLE_SHORT = { de: 'Ausverkauft', en: 'Sold out', fr: 'Épuisé', it: 'Esaurito' };
  var COMPACT_MAX_LINES = 2;

  // opts.discontinued: Produkt trägt das Auslaufend-Flag (variants_data._disc,
  // aus dem Shopify-Tag «auslaufend») — ausverkaufte Varianten zeigen dann die
  // Kurzform ohne Nachschub-Versprechen.
  function renderCompact(stockBreakdown, opts) {
    opts = opts || {};
    if (!stockBreakdown || stockBreakdown.length === 0) {
      var dict = opts.discontinued ? UNAVAILABLE_SHORT : UNAVAILABLE_COMPACT;
      var txt = dict[ACTIVE_LOCALE] || dict.de;
      return '<span class="hp-card__delivery-line hp-card__delivery-line--unavailable">' +
        '<span class="hp-card__delivery-ico">' + bellSvg(13) + '</span>' +
        '<span class="hp-card__delivery-txt">' + txt + '</span></span>';
    }
    var ordered = stockBreakdown.slice().sort(function (a, b) { return priorityOf(a) - priorityOf(b); });
    // Zweite Zeile nur, wenn die schnellste Stufe nicht schon «10+» zeigt (Entscheid
    // Tobias 2026-09-03; gleiche Regel wie hp-delivery.js auf der PDP). mto ausgenommen.
    var firstMeta = ordered.length ? lookupLabel(ordered[0].label_key) : null;
    var maxLines = (firstMeta && !firstMeta.mto && ordered[0].qty > QTY_DISPLAY_CAP) ? 1 : COMPACT_MAX_LINES;
    return ordered.slice(0, maxLines).map(function (b) {
      var meta = lookupLabel(b.label_key);
      if (!meta) return '';
      // «Produktion auf Bestellung»: keine Stückzahl, Monats-Suffix aus dem
      // Metafeld («Produktion auf Bestellung (3-4 Monate)», Wortlaut Tobias).
      var lineText = meta.mto
        ? meta.label_standalone + mtoCompactSuffix(b.months_min, b.months_max)
        : (b.qty > QTY_DISPLAY_CAP ? (MORE_THAN_CAP[ACTIVE_LOCALE] || MORE_THAN_CAP.de) : b.qty) + ' ' + meta.label_lower;
      return '<span class="hp-card__delivery-line" style="color:' + (meta.color || '') + '">' +
        '<span class="hp-card__delivery-ico">' + checkSvg(meta.icon_color, 11) + '</span>' +
        '<span class="hp-card__delivery-txt">' + lineText + '</span>' +
        '</span>';
    }).join('');
  }

  // --- Delivery ETA (shared by PDP box + SliderCart, 2026-06-09) -----------
  // Business rules: order cut-off 17:00 Europe/Zurich, shipping Mon–Fri, delivery
  // the next working day, no Saturday delivery. Blocked dates (holidays / "Tage
  // ohne Versand") come from window.HP_BLOCKED_DAYS (array of "YYYY-MM-DD"), fed
  // by the HPB "Feiertage und Tage ohne Versand" page (HIG-151) — treated like
  // weekends (no shipping AND no delivery). Hedged with "voraussichtlich".
  var ETA_STR = {
    de: { tomorrow: 'Zustellung voraussichtlich morgen', on: 'Zustellung voraussichtlich %D', window: 'Zustellung voraussichtlich %A bis %B', ready: 'Zustellung in 1 Werktag, sobald versandfertig' },
    en: { tomorrow: 'Delivery expected tomorrow', on: 'Delivery expected %D', window: 'Delivery expected %A to %B', ready: 'Delivery in 1 business day, once ready to ship' },
    fr: { tomorrow: 'Livraison prévue demain', on: 'Livraison prévue %D', window: 'Livraison prévue entre %A et %B', ready: 'Livraison en 1 jour ouvré, dès l’expédition' },
    it: { tomorrow: 'Consegna prevista domani', on: 'Consegna prevista %D', window: 'Consegna prevista tra %A e %B', ready: 'Consegna in 1 giorno lavorativo, una volta pronto' },
  };
  var CUTOFF_HOUR = 17;
  // Default blocked days = legal holidays in the canton of Schwyz (HighPowered is in
  // Rothenthurm SZ; the SZ list already includes the national ones). No shipping AND
  // no delivery on these. 2026 verified against sz.ch; 2027 computed (Easter 2027 =
  // 28.03). The editable HPB "Feiertage und Tage ohne Versand" page (HIG-151) will
  // override this via window.HP_BLOCKED_DAYS once live (so Tobias can add ad-hoc
  // no-shipping days + Rothenthurm-specific feasts). "voraussichtlich" hedges it.
  var DEFAULT_BLOCKED_DAYS = [
    // 2026
    '2026-01-01','2026-01-02','2026-01-06','2026-03-19','2026-04-03','2026-04-06',
    '2026-05-14','2026-05-25','2026-06-04','2026-08-01','2026-08-15','2026-11-01',
    '2026-12-08','2026-12-25','2026-12-26',
    // 2027
    '2027-01-01','2027-01-02','2027-01-06','2027-03-19','2027-03-26','2027-03-29',
    '2027-05-06','2027-05-17','2027-05-27','2027-08-01','2027-08-15','2027-11-01',
    '2027-12-08','2027-12-25','2027-12-26',
  ];
  function blockedDays() { return (window.HP_BLOCKED_DAYS && window.HP_BLOCKED_DAYS.length) ? window.HP_BLOCKED_DAYS : DEFAULT_BLOCKED_DAYS; }
  function iso(dt) { return dt.getUTCFullYear() + '-' + ('0' + (dt.getUTCMonth() + 1)).slice(-2) + '-' + ('0' + dt.getUTCDate()).slice(-2); }
  function nowZurich() {
    var p = {};
    new Intl.DateTimeFormat('en-US', { timeZone: 'Europe/Zurich', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', hour12: false })
      .formatToParts(new Date()).forEach(function (x) { p[x.type] = x.value; });
    return { y: +p.year, m: +p.month, d: +p.day, hour: (+p.hour) % 24 };
  }
  function mkDate(y, m, d) { return new Date(Date.UTC(y, m - 1, d)); }
  function addDays(dt, n) { return new Date(dt.getTime() + n * 86400000); }
  function isBlocked(dt) { return blockedDays().indexOf(iso(dt)) !== -1; }
  function isWorkday(dt) { var w = dt.getUTCDay(); return w >= 1 && w <= 5 && !isBlocked(dt); }
  function nextWorkday(dt) { var d = addDays(dt, 1); while (!isWorkday(d)) { d = addDays(d, 1); } return d; }
  function fmtDate(dt) {
    var wd = '';
    try { wd = new Intl.DateTimeFormat(ACTIVE_LOCALE, { timeZone: 'UTC', weekday: 'short' }).format(dt).replace(/\.$/, ''); } catch (e) { wd = ''; }
    var dd = ('0' + dt.getUTCDate()).slice(-2);
    var mm = ('0' + (dt.getUTCMonth() + 1)).slice(-2);
    return (wd ? wd + ', ' : '') + dd + '.' + mm + '.';
  }
  var RELATIVE_DAY_STR = {
    de: { today: 'heute', tomorrow: 'morgen', day_after_tomorrow: 'übermorgen' },
    en: { today: 'today', tomorrow: 'tomorrow' },
    fr: { today: 'aujourd’hui', tomorrow: 'demain', day_after_tomorrow: 'après-demain' },
    it: { today: 'oggi', tomorrow: 'domani', day_after_tomorrow: 'dopodomani' },
  };
  // Kalendertag-Abstand zum heutigen Datum in Europe/Zurich. Die Lieferdaten
  // selbst sind UTC-Kalendertage, damit Sommerzeitwechsel den Abstand nicht
  // verfälschen.
  function relativeDayDistance(dt) {
    var z = nowZurich();
    var today = mkDate(z.y, z.m, z.d);
    var date = mkDate(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
    return Math.round((date.getTime() - today.getTime()) / 86400000);
  }
  function fmtWeekday(dt) {
    try { return new Intl.DateTimeFormat(ACTIVE_LOCALE, { timeZone: 'UTC', weekday: 'long' }).format(dt); } catch (e) { return fmtDate(dt); }
  }
  // Relative Leiter für Zustelldaten; forceDate hält beide Grenzen eines
  // weiter entfernten Fensters bewusst im gleichen Datumsformat.
  function fmtRelative(dt, forceDate) {
    if (forceDate === true) return fmtDate(dt);
    var distance = relativeDayDistance(dt);
    var S = RELATIVE_DAY_STR[ACTIVE_LOCALE] || RELATIVE_DAY_STR.de;
    if (distance === 0) return S.today;
    if (distance === 1) return S.tomorrow;
    if (distance === 2 && ACTIVE_LOCALE !== 'en') return S.day_after_tomorrow;
    if (distance >= 2 && distance <= 6) return fmtWeekday(dt);
    return fmtDate(dt);
  }
  // Zustelldatum sofort versandfertiger Ware: Versand heute, wenn Werktag und
  // vor 17 Uhr, sonst am nächsten Werktag; Zustellung am Werktag danach.
  function instantDelivery(z) {
    var today = mkDate(z.y, z.m, z.d);
    var ship = (isWorkday(today) && z.hour < CUTOFF_HOUR) ? today : nextWorkday(today);
    return nextWorkday(ship);
  }
  function computeEta() {
    var z = nowZurich();
    var today = mkDate(z.y, z.m, z.d);
    var delivery = instantDelivery(z);
    var tomorrow = addDays(today, 1);
    return { isTomorrow: delivery.getTime() === tomorrow.getTime(), dateStr: fmtRelative(delivery) };
  }
  // hasInstant true -> relative Zustellangabe; false -> Text ab Versandbereitschaft.
  function etaText(hasInstant) {
    var L = ETA_STR[ACTIVE_LOCALE] || ETA_STR.de;
    if (hasInstant) { var info = computeEta(); return L.on.replace('%D', info.dateStr); }
    return L.ready;
  }

  // --- Zustellfenster pro Stufe (Lieferzeit-Redesign 2026-07, LIEFERZEIT_KONZEPT.md) ---
  // Kalendertage-Spannen der Stufen (Bestelltag zählt nicht, Tag 1 = morgen):
  // Versandbereit-Fenster = heute + min … heute + max KALENDERTAGE. Versand am
  // ersten Werktag AB Bereitschaft, Zustellung am darauffolgenden Werktag
  // (Sa/So + HP_BLOCKED_DAYS übersprungen). 17-Uhr-Cutoff gilt NUR für instant.
  // over_12_weeks, in_over_12_weeks + made_to_order haben bewusst KEIN
  // Tagesfenster (ein Zustellfenster Monate im Voraus wäre Scheingenauigkeit).
  var LABEL_DAYS = {
    instant: [0, 0], in_1_2_days: [1, 2], in_2_3_days: [2, 3], in_3_4_days: [3, 4],
    in_4_5_days: [4, 5], in_5_6_days: [5, 6], in_6_7_days: [6, 7], in_7_9_days: [7, 9],
    in_10_14_days: [10, 14], in_15_20_days: [15, 20], in_3_4_weeks: [21, 28],
    in_5_6_weeks: [35, 42], in_7_9_weeks: [49, 63], in_10_12_weeks: [70, 84],
  };
  function firstWorkdayOnOrAfter(dt) { var d = dt; while (!isWorkday(d)) { d = addDays(d, 1); } return d; }
  function deliveryForReadyIn(days) {
    var z = nowZurich();
    var today = mkDate(z.y, z.m, z.d);
    var ship = firstWorkdayOnOrAfter(addDays(today, days));
    return nextWorkday(ship);
  }
  // «Produktion auf Bestellung»: Monats-Texte (Wortlaute Tobias 2026-07-29).
  // %A/%B = Monate min/max. compact = Card/Cart-Suffix, long = PDP-Statuszeile-
  // Suffix, header = Cart-Kopfzeile.
  var MTO_STR = {
    de: { unit: 'Monate', long_one: 'Lieferzeit ca. %A Monate', long_range: 'Lieferzeit ca. %A bis %B Monate', header_one: 'Lieferung in ca. %A Monaten', header_range: 'Lieferung in ca. %A bis %B Monaten' },
    en: { unit: 'months', long_one: 'delivery time approx. %A months', long_range: 'delivery time approx. %A to %B months', header_one: 'Delivery in approx. %A months', header_range: 'Delivery in approx. %A to %B months' },
    fr: { unit: 'mois', long_one: 'délai de livraison env. %A mois', long_range: 'délai de livraison env. %A à %B mois', header_one: 'Livraison dans env. %A mois', header_range: 'Livraison dans env. %A à %B mois' },
    it: { unit: 'mesi', long_one: 'tempi di consegna ca. %A mesi', long_range: 'tempi di consegna ca. %A-%B mesi', header_one: 'Consegna in ca. %A mesi', header_range: 'Consegna in ca. %A-%B mesi' },
  };
  function mtoStr() { return MTO_STR[ACTIVE_LOCALE] || MTO_STR.de; }
  function mtoNorm(min, max) {
    if (min == null && max == null) return null;
    if (min == null) min = max;
    if (max == null) max = min;
    return [min, max];
  }
  // '(3-4 Monate)' bzw. '(3 Monate)' — Suffix für Cards, Quick-Add und Cart-Property.
  function mtoCompactSuffix(min, max) {
    var mm = mtoNorm(min, max);
    if (!mm) return '';
    var u = mtoStr().unit;
    return ' (' + (mm[0] === mm[1] ? mm[0] : mm[0] + '-' + mm[1]) + ' ' + u + ')';
  }
  // '(Lieferzeit ca. 3 bis 4 Monate)' — Suffix für die PDP-Statuszeile.
  function mtoLongSuffix(min, max) {
    var mm = mtoNorm(min, max);
    if (!mm) return '';
    var S = mtoStr();
    var t = mm[0] === mm[1] ? S.long_one.replace('%A', mm[0]) : S.long_range.replace('%A', mm[0]).replace('%B', mm[1]);
    return ' (' + t + ')';
  }
  // 'Lieferung in ca. 3 bis 4 Monaten' — Cart-Kopfzeile bei made_to_order.
  function mtoHeaderText(min, max) {
    var mm = mtoNorm(min, max);
    var S = mtoStr();
    if (!mm) mm = [3, 4];
    return mm[0] === mm[1] ? S.header_one.replace('%A', mm[0]) : S.header_range.replace('%A', mm[0]).replace('%B', mm[1]);
  }
  // Zustellfenster-Text für eine Stufe: instant = heutige Logik (Cutoff),
  // made_to_order = Monats-Header, Tages-/Wochen-Stufe = Datumsbereich,
  // over_12_weeks/unbekannt = null (Zeile verstecken).
  function etaWindowText(labelKey, monthsMin, monthsMax) {
    if (labelKey === 'instant') return etaText(true);
    if (labelKey === 'made_to_order') return mtoHeaderText(monthsMin, monthsMax);
    var w = windowDates(labelKey);
    return w ? spanText(w[0], w[1]) : null;
  }
  // Zustelldaten [frühestens, spätestens] einer Stufe; null für Stufen ohne
  // Tagesfenster (made_to_order, über 12 Wochen, unbekannt).
  function windowDates(labelKey) {
    if (labelKey === 'instant') { var d0 = instantDelivery(nowZurich()); return [d0, d0]; }
    var d = LABEL_DAYS[labelKey];
    if (!d) return null;
    return [deliveryForReadyIn(d[0]), deliveryForReadyIn(d[1])];
  }
  // Text aus zwei Zustelldaten: gleiche Daten = ein Datum, sonst Bereich. Liegt
  // das Ende 7 oder mehr Tage entfernt, stehen beide Grenzen als Datum (keine
  // Mischung aus «morgen» und Datum).
  function spanText(a, b) {
    var L = ETA_STR[ACTIVE_LOCALE] || ETA_STR.de;
    if (a.getTime() === b.getTime()) {
      return L.on.replace('%D', fmtRelative(a));
    }
    var forceDate = relativeDayDistance(b) >= 7;
    return L.window.replace('%A', fmtRelative(a, forceDate)).replace('%B', fmtRelative(b, forceDate));
  }
  // Teillieferung (Wunsch Tobias 16.10.2026, CART_ZUSTELLUNG_FOOTER.md): Bereich
  // vom frühesten bis zum spätesten Zustelldatum über alle Stufen im Korb. Die
  // Sofort-Ware geht vorab, der Rest folgt mit der langsamsten Stufe. Hat eine
  // Stufe kein Tagesfenster, kommt null zurück und der Aufrufer bleibt beim
  // Text der langsamsten Stufe (etaWindowText).
  function etaRangeText(labelKeys) {
    var start = null, end = null;
    for (var i = 0; i < labelKeys.length; i++) {
      var w = windowDates(labelKeys[i]);
      if (!w) return null;
      if (!start || w[0].getTime() < start.getTime()) start = w[0];
      if (!end || w[1].getTime() > end.getTime()) end = w[1];
    }
    return start ? spanText(start, end) : null;
  }

  window.HPDeliveryCore = {
    LABEL_CATALOG: LABEL_CATALOG,
    activeLocale: ACTIVE_LOCALE,
    lookupLabel: lookupLabel,
    priorityOf: priorityOf,
    bellSvg: bellSvg,
    checkSvg: checkSvg,
    renderCompact: renderCompact,
    etaText: etaText,
    computeEta: computeEta,
    etaWindowText: etaWindowText,
    etaRangeText: etaRangeText,
    fmtDate: fmtDate,
    fmtRelative: fmtRelative,
    PILL_STR: PILL_STR,
    TEIL_STR: TEIL_STR,
    NUMBER_WORDS: NUMBER_WORDS,
    mtoCompactSuffix: mtoCompactSuffix,
    mtoLongSuffix: mtoLongSuffix,
    mtoHeaderText: mtoHeaderText,
    LABEL_DAYS: LABEL_DAYS,
    UNAVAILABLE_SHORT: UNAVAILABLE_SHORT,
    QTY_DISPLAY_CAP: QTY_DISPLAY_CAP,
    MORE_THAN_CAP: MORE_THAN_CAP,
  };
})();
